import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { promoConfig, promoMine, promoGenerate, promoSaveProfile } from "@/lib/api";
import { TemplatePicker } from "./TemplatePicker";
import { PromoForm } from "./PromoForm";
import { PromoPreview } from "./PromoPreview";
import { PromoVideoCard } from "./PromoVideoCard";
import { QueueMeter, fmtWait } from "@/components/promo/QueueMeter";
import { PromoAdmin } from "./PromoAdmin";

// Marketing → Media: OreVital AI Video Studio — pick a commercial → brand it (saved once) → preview → generate.
export default function PromoVideosTab({ userRecordId }) {
  const [cfg, setCfg] = useState(null);
  const [tplId, setTplId] = useState("");
  const [form, setForm] = useState(null);
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [meter, setMeter] = useState(null);
  const [notify, setNotify] = useState(true);
  const [autoReel] = useState(() => new URLSearchParams(window.location.search).get("reel") || "");
  const pollRef = useRef(null);
  const formRef = useRef(null);

  const loadVideos = useCallback(async () => {
    const r = await promoMine(userRecordId);
    if (r.meter) setMeter(r.meter);
    setVideos(r.videos);
    return r.videos;
  }, [userRecordId]);

  useEffect(() => {
    promoConfig(userRecordId).then(async (c) => {
      setCfg(c);
      if (!c.enabled) return;
      setForm(c.profile);
      setTplId(c.templates[0]?.id || "");
      await loadVideos();
    }).catch((e) => toast.error(e?.response?.data?.detail || "Could not load the video studio"));
  }, [userRecordId, loadVideos]);

  useEffect(() => {
    const active = videos.some((v) => v.status === "Queued" || v.status === "Rendering" || v.ig_post?.status === "publishing");
    clearInterval(pollRef.current);
    if (active) pollRef.current = setInterval(loadVideos, 4000);
    return () => clearInterval(pollRef.current);
  }, [videos, loadVideos]);

  function pick(id) {
    setTplId(id);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }

  async function generate() {
    setBusy(true);
    try {
      const r = await promoGenerate(userRecordId, userRecordId, tplId, form, notify);
      if (r.meter) setMeter(r.meter);
      const ahead = Math.max(0, (r.meter?.in_line || 1) - 1);
      toast.success(`Making your ${r.video.template_name} — ${ahead ? `${ahead} ahead of you, ` : ""}ready in ${fmtWait(r.meter?.new_job_wait_s)}${notify ? ". We'll text you." : ""}`);
      setVideos((v) => [r.video, ...v.filter((x) => x.id !== r.video.id)]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the video");
    } finally {
      setBusy(false);
    }
  }

  async function saveDetails() {
    setBusy(true);
    try {
      await promoSaveProfile(userRecordId, userRecordId, form);
      setCfg((c) => ({ ...c, profile_saved: true }));
      toast.success("Details saved — they'll be used on every video");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your details");
    } finally {
      setBusy(false);
    }
  }

  if (!cfg) return <div className="flex items-center gap-2 text-sm text-zinc-500 py-10 justify-center" data-testid="promo-loading"><Loader2 size={16} className="animate-spin" /> Loading…</div>;
  if (!cfg.enabled) return <div className="rounded-xl border border-zinc-200 bg-white p-6 text-sm text-zinc-600" data-testid="promo-disabled">The video studio is not enabled yet.</div>;
  if (!cfg.approved && !cfg.is_admin) return <div className="rounded-xl border border-amber-100 bg-amber-50 p-6 text-sm text-amber-800" data-testid="promo-not-approved">The video studio unlocks once your dealer account is approved.</div>;

  const tpl = cfg.templates.find((t) => t.id === tplId) || cfg.templates[0];
  const rendering = videos.some((v) => v.status === "Queued" || v.status === "Rendering");
  return (
    <div className="space-y-5" data-testid="promo-videos-tab">
      <div className="rounded-2xl bg-zinc-900 text-white p-5 sm:p-6 relative overflow-hidden" data-testid="promo-hero">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-300">🎬 Your spa. Your brand. Your commercial!</div>
        <h2 className="text-base md:text-lg font-semibold mt-1">Pick it. Brand it. Share it. Love it! ❤️</h2>
        <p className="text-sm text-zinc-300 mt-2 max-w-2xl">Introducing <b className="text-white">OreVital AI Video Studio™</b> — your personal marketing agency at your fingertips! Choose from our professionally produced peptide commercials, add your spa's logo, photo, business name and contact information, and create your own personalized video in minutes! Share it on Instagram, Facebook, SMS or email and let your brand shine. Professional video marketing has never been this easy! 🚀</p>
        {cfg.is_admin && (
          <button type="button" onClick={() => setShowAdmin((s) => !s)} className="absolute top-4 right-4 text-xs font-semibold rounded-full bg-white/10 hover:bg-white/20 px-3 h-8" data-testid="promo-admin-toggle">{showAdmin ? "Dealer view" : "Admin"}</button>
        )}
      </div>
      {showAdmin && cfg.is_admin ? <PromoAdmin caller={userRecordId} /> : (
        <>
          <TemplatePicker templates={cfg.templates} selected={tpl?.id} onSelect={pick} />
          {!cfg.narration_configured && <div className="rounded-xl border border-amber-100 bg-amber-50 p-3 text-xs text-amber-800" data-testid="promo-narration-missing">Narration is not configured yet — ask the admin to add the ElevenLabs key before generating.</div>}
          {form && tpl && (
            <div ref={formRef} className="space-y-5 scroll-mt-24">
              <QueueMeter meter={meter || cfg.meter} notify={notify} setNotify={setNotify} />
              <PromoForm form={form} setForm={setForm} userRecordId={userRecordId} onGenerate={generate} onSave={saveDetails} templateId={tpl.id} template={tpl} myVoices={cfg.my_voices || []} busy={busy} disabled={rendering || !cfg.narration_configured} templateName={tpl.name} saved={cfg.profile_saved} />
              <div className="max-w-[340px] w-full mx-auto"><PromoPreview form={form} templateId={tpl.id} /></div>
            </div>
          )}
          {videos.some((v) => v.status !== "Draft") && (
            <div className="space-y-3" data-testid="promo-my-videos">
              <h3 className="text-sm font-semibold text-zinc-900">My videos</h3>
              {videos.filter((v) => v.status !== "Draft").map((v) => <PromoVideoCard key={v.id} video={v} caller={userRecordId} onChange={loadVideos} autoReel={autoReel === v.id} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
